import { createContext, ReactNode, useContext, useMemo, useState } from 'react';

import { useEffect } from 'react';
import {
  buildKeycloakAuthorizeUrl,
  buildKeycloakLogoutUrl,
  buildKeycloakTokenUrl,
  createPkcePair,
  decodeJwtPayload,
  exchangeCodeForTokens,
  jwtIsExpired,
} from '../auth/oidc';
import { getStoredCardData, clearCardData, type CardUserInfo } from '../utils/cardDataDecoder';

const ENV = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env ?? {};

// Flag pour activer/désactiver complètement l'authentification
const AUTH_ENABLED = ENV.VITE_AUTH_ENABLED === 'true';

const CREDENTIALS_LOGIN_URL = ENV.VITE_AUTH_CREDENTIALS_URL ?? '/api/v1/biotransac/auth/login';
const EXTERNAL_LOGIN_URL =
  ENV.VITE_EXTERNAL_LOGIN_URL ?? 'http://localhost:8080/realms/unacoopec/account';

const OIDC_BASE_URL = ENV.VITE_OIDC_BASE_URL ?? 'http://localhost:8080';
const OIDC_REALM = ENV.VITE_OIDC_REALM ?? 'unacoopec';
const OIDC_CLIENT_ID = ENV.VITE_OIDC_CLIENT_ID ?? '';
const OIDC_TOKEN_URL =
  ENV.VITE_OIDC_TOKEN_URL ?? buildKeycloakTokenUrl({ baseUrl: OIDC_BASE_URL, realm: OIDC_REALM });
const OIDC_REDIRECT_PATH = ENV.VITE_OIDC_REDIRECT_PATH ?? '/auth/callback';

const PKCE_STORAGE_KEY = 'biotransac.oidc.pkce';
const TOKENS_STORAGE_KEY = 'biotransac.auth.tokens';

interface AuthUser {
  username: string;
  displayName: string;
  authMethod: 'credentials' | 'biometric';
  // Données de carte biométrique (si disponibles)
  cardData?: CardUserInfo;
}

interface CredentialsInput {
  username: string;
  password: string;
}

type AuthStatus = 'initializing' | 'authenticated' | 'unauthenticated';

interface StoredTokens {
  accessToken: string;
  idToken?: string;
  refreshToken?: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  status: AuthStatus;
  login: () => Promise<void>;
  loginWithCredentials: (input: CredentialsInput) => Promise<void>;
  loginFromBiometric: (displayName: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function buildAuthHeader(): string | null {
  const token =
    localStorage.getItem('authToken') ??
    localStorage.getItem('accessToken') ??
    localStorage.getItem('token');

  return token ? `Bearer ${token}` : null;
}

function readStoredUser(): AuthUser | null {
  const raw = localStorage.getItem('biotransac.auth.user');
  if (!raw) return null;

  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

function readStoredTokens(): StoredTokens | null {
  const raw = localStorage.getItem(TOKENS_STORAGE_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as StoredTokens;
  } catch {
    return null;
  }
}

function writeStoredTokens(tokens: StoredTokens | null) {
  if (!tokens) {
    localStorage.removeItem(TOKENS_STORAGE_KEY);
    return;
  }
  localStorage.setItem(TOKENS_STORAGE_KEY, JSON.stringify(tokens));
}

function buildUserFromIdToken(idToken?: string): AuthUser | null {
  if (!idToken) return null;
  const payload = decodeJwtPayload(idToken);
  if (!payload) return null;

  // Debug: afficher le contenu complet du token JWT
  console.log('[Auth] Payload JWT Keycloak:', payload);

  // Récupérer les données de carte biométrique si disponibles
  const cardData = getStoredCardData();
  console.log('[Auth] Données de carte récupérées:', cardData);

  // Si on a des données de carte, les utiliser en priorité
  if (cardData) {
    const username = cardData.code || (typeof payload.preferred_username === 'string' ? payload.preferred_username : 'Utilisateur');
    const displayName = `${cardData.prenoms} ${cardData.nom}`.trim() || (typeof payload.name === 'string' ? payload.name : 'Utilisateur');

    return {
      username,
      displayName,
      authMethod: 'biometric',
      cardData,
    };
  }

  // Sinon, utiliser les données du JWT Keycloak
  const username =
    (typeof payload.preferred_username === 'string' && payload.preferred_username) ||
    (typeof payload.email === 'string' && payload.email) ||
    (typeof payload.sub === 'string' && payload.sub) ||
    'Utilisateur';

  const displayName =
    (typeof payload.name === 'string' && payload.name) ||
    (typeof payload.given_name === 'string' && payload.given_name) ||
    username;

  return { username, displayName, authMethod: 'credentials' };
}

function isOidcConfigured() {
  return Boolean(OIDC_BASE_URL && OIDC_REALM && OIDC_CLIENT_ID);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() =>
    AUTH_ENABLED ? readStoredUser() : { username: 'dev', displayName: 'Mode Développement', authMethod: 'credentials' }
  );
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<AuthStatus>(AUTH_ENABLED ? 'initializing' : 'authenticated');

  const login = async () => {
    if (!AUTH_ENABLED) return; // Pas d'authentification si désactivée

    if (!isOidcConfigured()) {
      window.location.replace(EXTERNAL_LOGIN_URL);
      return;
    }

    const redirectUri = `${window.location.origin}${OIDC_REDIRECT_PATH}`;
    const { verifier, challenge, method } = await createPkcePair();
    // crypto.randomUUID() is not available in non-secure (HTTP) contexts
    const state = typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : Array.from(crypto.getRandomValues(new Uint8Array(16)))
          .map((b, i) => (i === 6 ? (b & 0x0f) | 0x40 : i === 8 ? (b & 0x3f) | 0x80 : b).toString(16).padStart(2, '0'))
          .join('')
          .replace(/(.{8})(.{4})(.{4})(.{4})(.{12})/, '$1-$2-$3-$4-$5');
    sessionStorage.setItem(PKCE_STORAGE_KEY, JSON.stringify({ verifier, state, redirectUri }));

    const authorizeUrl = buildKeycloakAuthorizeUrl({
      baseUrl: OIDC_BASE_URL,
      realm: OIDC_REALM,
      clientId: OIDC_CLIENT_ID,
      redirectUri,
      state,
      codeChallenge: challenge,
      codeChallengeMethod: method,
    });

    window.location.replace(authorizeUrl);
  };

  const loginWithCredentials = async ({ username, password }: CredentialsInput) => {
    if (!AUTH_ENABLED) return; // Pas d'authentification si désactivée

    if (isOidcConfigured()) {
      await login();
      return;
    }

    setLoading(true);

    try {
      // Bypass local en mode développement (admin / admin)
      if (ENV['MODE'] !== 'production' && username === 'admin' && password === 'admin') {
        await new Promise((r) => setTimeout(r, 400));
        const nextUser: AuthUser = {
          username: 'admin',
          displayName: 'Administrateur',
          authMethod: 'credentials',
        };
        localStorage.setItem('biotransac.auth.user', JSON.stringify(nextUser));
        setUser(nextUser);
        setStatus('authenticated');
        return;
      }

      const authHeader = buildAuthHeader();
      const response = await fetch(CREDENTIALS_LOGIN_URL, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...(authHeader ? { Authorization: authHeader } : {}),
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Identifiants invalides');
      }

      const nextUser: AuthUser = {
        username,
        displayName: username,
        authMethod: 'credentials',
      };

      localStorage.setItem('biotransac.auth.user', JSON.stringify(nextUser));
      setUser(nextUser);
      setStatus('authenticated');
    } finally {
      setLoading(false);
    }
  };

  const loginFromBiometric = (displayName: string) => {
    if (!AUTH_ENABLED) return; // Pas d'authentification si désactivée

    if (isOidcConfigured()) {
      void login();
      return;
    }

    const nextUser: AuthUser = {
      username: displayName,
      displayName,
      authMethod: 'biometric',
    };

    localStorage.setItem('biotransac.auth.user', JSON.stringify(nextUser));
    setUser(nextUser);
    setStatus('authenticated');
  };

  const logout = () => {
    console.log('[Auth] logout() appelé');
    console.log('[Auth] AUTH_ENABLED:', AUTH_ENABLED);

    if (!AUTH_ENABLED) {
      console.log('[Auth] Auth désactivée, logout annulé');
      return;
    }

    const stored = readStoredTokens();
    console.log('[Auth] Tokens stockés:', stored ? 'présents' : 'absents');

    // Nettoyer l'état local immédiatement
    localStorage.removeItem('biotransac.auth.user');
    writeStoredTokens(null);
    sessionStorage.removeItem(PKCE_STORAGE_KEY);
    clearCardData(); // Nettoyer les données de carte

    console.log('[Auth] isOidcConfigured():', isOidcConfigured());

    if (isOidcConfigured()) {
      // Marquer qu'on est en train de se déconnecter (pour éviter la redirection auto)
      sessionStorage.setItem('biotransac.logging_out', 'true');

      console.log('[Auth] ID Token présent:', !!stored?.idToken);
      if (stored?.idToken) {
        console.log('[Auth] ID Token (premiers 50 chars):', stored.idToken.substring(0, 50));
      }

      const logoutUrl = buildKeycloakLogoutUrl({
        baseUrl: OIDC_BASE_URL,
        realm: OIDC_REALM,
        postLogoutRedirectUri: `${window.location.origin}/`,
        idTokenHint: stored?.idToken ?? null,
      });
      console.log('[Auth] URL de logout:', logoutUrl);

      // Nettoyer l'état avant la redirection
      setUser(null);
      setStatus('unauthenticated');

      window.location.replace(logoutUrl);
    } else {
      console.log('[Auth] OIDC non configuré, redirection simple');
      setUser(null);
      setStatus('unauthenticated');
    }
  };

  useEffect(() => {
    // Si l'authentification est désactivée, on reste en mode authentifié
    if (!AUTH_ENABLED) {
      setStatus('authenticated');
      return;
    }

    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    const state = url.searchParams.get('state');

    const finalizeUnauth = () => {
      setStatus('unauthenticated');
      setLoading(false);
    };

    const bootstrap = async () => {
      if (code && state) {
        if (!isOidcConfigured()) {
          finalizeUnauth();
          return;
        }

        setLoading(true);
        try {
          const rawPkce = sessionStorage.getItem(PKCE_STORAGE_KEY);
          sessionStorage.removeItem(PKCE_STORAGE_KEY);
          const pkce = rawPkce ? (JSON.parse(rawPkce) as { verifier: string; state: string; redirectUri: string }) : null;

          if (!pkce || pkce.state !== state) {
            throw new Error('OIDC state mismatch');
          }

          const tokens = await exchangeCodeForTokens({
            tokenUrl: OIDC_TOKEN_URL,
            clientId: OIDC_CLIENT_ID,
            code,
            redirectUri: pkce.redirectUri,
            codeVerifier: pkce.verifier,
          });

          if (!tokens.access_token) throw new Error('Access token manquant');

          const storedTokens: StoredTokens = {
            accessToken: tokens.access_token,
            idToken: tokens.id_token,
            refreshToken: tokens.refresh_token,
          };

          writeStoredTokens(storedTokens);
          const nextUser = buildUserFromIdToken(tokens.id_token) ?? readStoredUser();
          if (nextUser) {
            localStorage.setItem('biotransac.auth.user', JSON.stringify(nextUser));
            setUser(nextUser);
          }

          setStatus('authenticated');

          url.searchParams.delete('code');
          url.searchParams.delete('state');
          url.searchParams.delete('session_state');
          window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
        } catch {
          writeStoredTokens(null);
          localStorage.removeItem('biotransac.auth.user');
          setUser(null);
          finalizeUnauth();
          return;
        } finally {
          setLoading(false);
        }
        return;
      }

      const stored = readStoredTokens();
      if (stored?.accessToken && !jwtIsExpired(stored.accessToken)) {
        const existingUser = readStoredUser() ?? buildUserFromIdToken(stored.idToken);
        if (existingUser) setUser(existingUser);
        setStatus('authenticated');
        return;
      }

      finalizeUnauth();
    };

    void bootstrap();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: status === 'authenticated',
      loading,
      status,
      login,
      loginWithCredentials,
      loginFromBiometric,
      logout,
    }),
    [user, loading, status]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
